import { ReactElement, useState, useRef, useEffect } from 'react'
import {
  createStyles,
  makeStyles,
  Fab,
  Paper,
  Typography,
  TextField,
  IconButton,
  Box,
  CircularProgress,
  Zoom,
  Theme
} from '@material-ui/core'
import { Close, Send, QuestionAnswer } from '@material-ui/icons'
import { ChatMessage, sendChatMessage } from 'ustaxes/chatbot/api'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fab: {
      position: 'fixed',
      bottom: theme.spacing(3),
      right: theme.spacing(3),
      zIndex: theme.zIndex.modal + 1,
      width: 56,
      height: 56
    },
    chatPanel: {
      position: 'fixed',
      bottom: theme.spacing(11),
      right: theme.spacing(3),
      width: 380,
      height: 520,
      display: 'flex',
      flexDirection: 'column',
      zIndex: theme.zIndex.modal,
      borderRadius: 16,
      overflow: 'hidden',
      boxShadow: '0 12px 40px rgba(26, 35, 126, 0.2)',
      border:
        theme.palette.type === 'dark'
          ? '1px solid rgba(255,255,255,0.08)'
          : '1px solid rgba(26, 35, 126, 0.08)',
      [theme.breakpoints.down('xs')]: {
        width: 'calc(100vw - 32px)',
        height: 'calc(100vh - 120px)',
        bottom: theme.spacing(10),
        right: 16
      }
    },
    chatHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(1.5, 2),
      background: 'linear-gradient(135deg, #1A237E 0%, #283593 100%)',
      color: '#FFFFFF'
    },
    headerTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1)
    },
    headerTitleText: {
      fontWeight: 700,
      fontSize: '0.95rem'
    },
    headerSubtext: {
      fontSize: '0.7rem',
      opacity: 0.8
    },
    closeButton: {
      color: 'rgba(255,255,255,0.8)',
      padding: 4,
      '&:hover': {
        color: '#FFFFFF',
        backgroundColor: 'rgba(255,255,255,0.1)'
      }
    },
    messageArea: {
      flex: 1,
      overflowY: 'auto',
      padding: theme.spacing(2),
      backgroundColor:
        theme.palette.type === 'dark' ? '#0F1229' : '#FAFAFA',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5)
    },
    welcomeMessage: {
      textAlign: 'center',
      padding: theme.spacing(4, 2),
      color: theme.palette.text.secondary
    },
    welcomeIcon: {
      fontSize: 40,
      color: theme.palette.primary.main,
      marginBottom: theme.spacing(1)
    },
    userMessage: {
      alignSelf: 'flex-end',
      background: 'linear-gradient(135deg, #1A237E 0%, #283593 100%)',
      color: '#FFFFFF',
      borderRadius: '14px 14px 4px 14px',
      padding: theme.spacing(1.25, 2),
      maxWidth: '82%',
      fontSize: '0.88rem',
      lineHeight: 1.5,
      boxShadow: '0 2px 6px rgba(26, 35, 126, 0.2)'
    },
    assistantMessage: {
      alignSelf: 'flex-start',
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255,255,255,0.06)'
          : '#FFFFFF',
      color: theme.palette.text.primary,
      borderRadius: '14px 14px 14px 4px',
      padding: theme.spacing(1.25, 2),
      maxWidth: '82%',
      fontSize: '0.88rem',
      lineHeight: 1.5,
      boxShadow:
        theme.palette.type === 'dark'
          ? 'none'
          : '0 1px 4px rgba(0,0,0,0.06)',
      border:
        theme.palette.type === 'dark'
          ? '1px solid rgba(255,255,255,0.06)'
          : '1px solid rgba(26, 35, 126, 0.04)'
    },
    inputArea: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1, 1.5),
      backgroundColor: theme.palette.background.paper,
      borderTop:
        theme.palette.type === 'dark'
          ? '1px solid rgba(255,255,255,0.06)'
          : '1px solid rgba(26, 35, 126, 0.06)',
      gap: theme.spacing(1)
    },
    input: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 20,
        fontSize: '0.88rem',
        backgroundColor:
          theme.palette.type === 'dark'
            ? 'rgba(255,255,255,0.04)'
            : 'rgba(26, 35, 126, 0.03)'
      },
      '& .MuiOutlinedInput-input': {
        padding: '10px 16px'
      }
    },
    sendButton: {
      color: theme.palette.primary.main,
      padding: 8,
      '&:disabled': {
        color: theme.palette.text.secondary
      }
    },
    loadingDots: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      padding: theme.spacing(1, 0)
    },
    typingIndicator: {
      alignSelf: 'flex-start',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      padding: theme.spacing(1, 2),
      borderRadius: 14,
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255,255,255,0.06)'
          : '#FFFFFF',
      border:
        theme.palette.type === 'dark'
          ? '1px solid rgba(255,255,255,0.06)'
          : '1px solid rgba(26, 35, 126, 0.04)'
    },
    quickActions: {
      display: 'flex',
      gap: theme.spacing(1),
      flexWrap: 'wrap',
      justifyContent: 'center',
      marginTop: theme.spacing(2)
    },
    quickAction: {
      fontSize: '0.75rem',
      padding: '6px 12px',
      borderRadius: 16,
      cursor: 'pointer',
      border:
        theme.palette.type === 'dark'
          ? '1px solid rgba(255,255,255,0.12)'
          : '1px solid rgba(26, 35, 126, 0.12)',
      backgroundColor: 'transparent',
      color: theme.palette.text.secondary,
      fontFamily: 'inherit',
      transition: 'all 0.15s ease',
      '&:hover': {
        backgroundColor:
          theme.palette.type === 'dark'
            ? 'rgba(255,255,255,0.06)'
            : 'rgba(26, 35, 126, 0.04)',
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main
      }
    }
  })
)

const quickQuestions = [
  'What is a W-2?',
  'Standard vs itemized deductions?',
  'How do tax credits work?',
  'What can I deduct?'
]

const ChatWidget = (): ReactElement => {
  const classes = useStyles()
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSend = async (text?: string) => {
    const messageText = text ?? input.trim()
    if (!messageText || isLoading) return

    const userMessage: ChatMessage = { role: 'user', content: messageText }
    const newMessages = [...messages, userMessage]
    setMessages(newMessages)
    setInput('')
    setIsLoading(true)

    try {
      const response = await sendChatMessage(newMessages)
      setMessages([
        ...newMessages,
        { role: 'assistant', content: response }
      ])
    } catch {
      setMessages([
        ...newMessages,
        {
          role: 'assistant',
          content:
            "I'm having trouble connecting right now. Please try again in a moment."
        }
      ])
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      {/* FAB */}
      <Zoom in={!isOpen}>
        <Fab
          color="primary"
          className={classes.fab}
          onClick={() => setIsOpen(true)}
          aria-label="open tax assistant"
        >
          <QuestionAnswer />
        </Fab>
      </Zoom>

      {/* Chat Panel */}
      {isOpen && (
        <Paper className={classes.chatPanel} elevation={0}>
          {/* Header */}
          <Box className={classes.chatHeader}>
            <Box className={classes.headerTitle}>
              <QuestionAnswer style={{ fontSize: 20 }} />
              <Box>
                <Typography className={classes.headerTitleText}>
                  Tax Assistant
                </Typography>
                <Typography className={classes.headerSubtext}>
                  Powered by Ameritax AI
                </Typography>
              </Box>
            </Box>
            <IconButton
              className={classes.closeButton}
              onClick={() => setIsOpen(false)}
              size="small"
              aria-label="close chat"
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box className={classes.messageArea}>
            {messages.length === 0 && (
              <Box className={classes.welcomeMessage}>
                <QuestionAnswer className={classes.welcomeIcon} />
                <Typography
                  variant="body1"
                  style={{ fontWeight: 600, marginBottom: 4 }}
                >
                  Hi! I&apos;m your tax assistant.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Ask me anything about US tax filing, deductions, credits,
                  or how to use Ameritax.
                </Typography>
                <Box className={classes.quickActions}>
                  {quickQuestions.map((q) => (
                    <button
                      key={q}
                      className={classes.quickAction}
                      onClick={() => void handleSend(q)}
                    >
                      {q}
                    </button>
                  ))}
                </Box>
              </Box>
            )}

            {messages.map((msg, i) => (
              <Box
                key={i}
                className={
                  msg.role === 'user'
                    ? classes.userMessage
                    : classes.assistantMessage
                }
              >
                <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                  {msg.content}
                </Typography>
              </Box>
            ))}

            {isLoading && (
              <Box className={classes.typingIndicator}>
                <CircularProgress size={14} thickness={5} />
                <Typography variant="caption" color="textSecondary">
                  Thinking...
                </Typography>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* Input */}
          <Box className={classes.inputArea}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Ask a tax question..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  void handleSend()
                }
              }}
              className={classes.input}
              disabled={isLoading}
              autoComplete="off"
            />
            <IconButton
              className={classes.sendButton}
              onClick={() => void handleSend()}
              disabled={isLoading || !input.trim()}
              size="small"
              aria-label="send message"
            >
              <Send fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      )}
    </>
  )
}

export default ChatWidget
